import { useState } from 'react'
import {
  Boxes,
  ChevronDown,
  ChevronRight,
  Eye,
  EyeOff,
  QrCode,
  MapPin,
  Spline,
  Layers,
  PenSquare,
  Sparkles,
  Trash2,
} from 'lucide-react'
import clsx from 'clsx'
import { LAYER, useSpatialStore } from '@/features/spatial-model/store.js'

const SECTIONS = [
  { key: LAYER.ASSET, label: 'Assets', icon: Boxes, list: 'assets' },
  { key: LAYER.NODES, label: 'Nodes', icon: Sparkles, list: 'nodes', selKind: 'node' },
  { key: LAYER.EDGES, label: 'Edges', icon: Spline, list: 'edges', selKind: 'edge' },
  { key: LAYER.ANCHORS, label: 'Anchors', icon: QrCode, list: 'anchors', selKind: 'anchor' },
  { key: LAYER.DESTINATIONS, label: 'Destinations', icon: MapPin, list: 'destinations', selKind: 'destination' },
  { key: LAYER.ZONES, label: 'Zones', icon: PenSquare, list: 'zones', selKind: 'zone' },
]

export default function WorkspaceTree() {
  const layers = useSpatialStore((s) => s.layers)
  const toggleLayer = useSpatialStore((s) => s.toggleLayer)
  const graph = useSpatialStore((s) => s.graph)
  const floor = useSpatialStore((s) => s.floor)
  const selection = useSpatialStore((s) => s.selection)
  const setSelection = useSpatialStore((s) => s.setSelection)
  const removers = {
    node: useSpatialStore((s) => s.removeNode),
    edge: useSpatialStore((s) => s.removeEdge),
    anchor: useSpatialStore((s) => s.removeAnchor),
    destination: useSpatialStore((s) => s.removeDestination),
    zone: useSpatialStore((s) => s.removeZone),
    beacon: useSpatialStore((s) => s.removeBeacon),
  }

  const [open, setOpen] = useState(() => Object.fromEntries(SECTIONS.map((s) => [s.key, true])))

  return (
    <div>
      <div className="panel-header">
        <div className="panel-header__title">Object Tree</div>
        <Layers size={14} color="var(--text-muted)" />
      </div>
      <div className="tree-section">
        {SECTIONS.map((section) => {
          const items = section.list === 'assets'
            ? floor?.assets || []
            : graph[section.list] || []
          const isOpen = open[section.key]
          const visible = layers[section.key]
          return (
            <div key={section.key} style={{ marginBottom: 4 }}>
              <div className="tree-row" style={{ fontWeight: 600, color: 'var(--text-primary)' }}>
                <button
                  type="button"
                  className="btn btn--ghost btn--sm"
                  style={{ padding: 0, marginRight: 4 }}
                  onClick={() => setOpen((s) => ({ ...s, [section.key]: !s[section.key] }))}
                >
                  {isOpen ? <ChevronDown size={14} /> : <ChevronRight size={14} />}
                </button>
                <section.icon />
                <span style={{ flex: 1 }}>{section.label}</span>
                <span className="badge">{items.length}</span>
                <button
                  type="button"
                  className="btn btn--ghost btn--sm"
                  style={{ padding: 0, marginLeft: 4 }}
                  title={visible ? 'Hide layer' : 'Show layer'}
                  onClick={() => toggleLayer(section.key)}
                >
                  {visible ? <Eye size={14} /> : <EyeOff size={14} />}
                </button>
              </div>
              {isOpen && (
                <div style={{ paddingLeft: 22 }}>
                  {items.length === 0 && (
                    <div className="muted" style={{ padding: '4px 10px', fontSize: 12 }}>None yet</div>
                  )}
                  {items.map((item) => {
                    const isSelected = section.selKind && selection?.kind === section.selKind && selection.id === item.id
                    const remove = section.selKind ? removers[section.selKind] : null
                    return (
                      <div
                        key={item.id}
                        className={clsx('tree-row', isSelected && 'is-active')}
                        role={section.selKind ? 'button' : undefined}
                        tabIndex={section.selKind ? 0 : -1}
                        onClick={() => section.selKind && setSelection({ kind: section.selKind, id: item.id })}
                        onKeyDown={(e) => {
                          if (!section.selKind) return
                          if (e.key === 'Enter' || e.key === ' ') {
                            e.preventDefault()
                            setSelection({ kind: section.selKind, id: item.id })
                          }
                        }}
                      >
                        <span className="muted" style={{ fontFamily: 'var(--font-mono)', fontSize: 11 }}>
                          {(item.id || '').slice(0, 6)}
                        </span>
                        <span style={{ flex: 1, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>
                          {item.name || item.label || section.label.slice(0, -1)}
                        </span>
                        {item.node_type && <span className={`tag tag--${section.selKind}`}>{item.node_type}</span>}
                        {item.anchor_type && <span className={`tag tag--${section.selKind}`}>{item.anchor_type}</span>}
                        {item.zone_type && <span className={`tag tag--${section.selKind}`}>{item.zone_type}</span>}
                        {remove && (
                          <button
                            type="button"
                            className="tree-row__delete"
                            aria-label={`Delete ${section.selKind}`}
                            title={`Delete ${section.selKind}`}
                            onClick={(e) => {
                              e.stopPropagation()
                              remove(item.id)
                            }}
                          >
                            <Trash2 size={13} />
                          </button>
                        )}
                      </div>
                    )
                  })}
                </div>
              )}
            </div>
          )
        })}
      </div>
    </div>
  )
}
